import api from '../client';

/**
 * Service pour l'authentification
 */
const authService = {
  /**
   * Inscription d'un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur (email, password, firstName, lastName, organizationName)
   * @returns {Promise} Données de l'utilisateur et tokens
   */
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);

      // Sauvegarder les tokens et l'utilisateur.
      // Le backend renvoie les tokens à plat (accessToken/refreshToken) ; on gère
      // aussi le cas imbriqué dans `tokens` par sécurité (même logique que login).
      if (response.data.success && response.data.data) {
        const responseData = response.data.data;
        const accessToken = responseData.accessToken || responseData.tokens?.accessToken;
        const refreshToken = responseData.refreshToken || responseData.tokens?.refreshToken;
        const user = responseData.user;

        if (accessToken) localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        if (user) localStorage.setItem('user', JSON.stringify(user));
      }

      // Cohérent avec login() : on renvoie la réponse axios complète.
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Connexion d'un utilisateur
   * @param {Object} credentials - Email et mot de passe
   * @returns {Promise} Données de l'utilisateur et tokens
   */
  login: async (credentials) => {
    try {
      const response = await api.post('/auth/login', credentials);
      
      // Sauvegarder les tokens et l'utilisateur
      if (response.data.success && response.data.data) {
        const responseData = response.data.data;
        
        // Gérer différentes structures de réponse
        let accessToken, refreshToken, user;
        
        // Cas 1: Les tokens sont dans un objet 'tokens'
        if (responseData.tokens) {
          accessToken = responseData.tokens.accessToken;
          refreshToken = responseData.tokens.refreshToken;
          user = responseData.user;
        }
        // Cas 2: Les tokens sont directement dans responseData
        else if (responseData.accessToken) {
          accessToken = responseData.accessToken;
          refreshToken = responseData.refreshToken;
          user = responseData.user;
        }
        
        // Sauvegarder si les données sont présentes
        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
        }
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }
        if (user) {
          localStorage.setItem('user', JSON.stringify(user));
        }
      }
      
      return response;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Déconnexion de l'utilisateur
   */
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Nettoyer le localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  /**
   * Rafraîchir le token d'accès
   * @param {string} refreshToken - Token de rafraîchissement
   * @returns {Promise} Nouveaux tokens
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await api.post('/auth/refresh', { refreshToken });
      
      if (response.data.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer l'utilisateur actuel depuis le localStorage
   * @returns {Object|null} Utilisateur ou null
   */
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  /**
   * Vérifier si l'utilisateur est authentifié
   * @returns {boolean} True si authentifié
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('accessToken');
  },
};

export default authService;
